import type { AppUser } from "@/context/AuthContext";

/**
 * Fonte ÚNICA de verdade para decidir se um usuário tem acesso pago.
 * Usada por LoginPage e ProtectedRoute para evitar divergência.
 *
 * Considera ativo se:
 *  - role === 'admin', OU
 *  - tem plan pago (qualquer valor != 'free' e != null), OU
 *  - tem subscription_end_date no futuro (mesmo que is_subscribed esteja desatualizado)
 *
 * Critério tolerante: basta UM dos sinais (plan pago OU data futura OU is_subscribed)
 * para evitar bloquear assinantes legítimos por flag dessincronizada.
 */
export const isActiveSubscriber = (u: AppUser | null | undefined): boolean => {
  if (!u) return false;
  if (u.role === "admin") return true;

  // REGRA DE VENCIMENTO (prioridade máxima):
  // se existe data de validade e ela já passou, o acesso é suspenso,
  // mesmo que plan/is_subscribed ainda estejam desatualizados no perfil.
  if (isExpiredSubscriber(u)) return false;

  const hasPaidPlan = !!u.plan && u.plan !== "free";
  const hasFutureEndDate =
    !!u.subscriptionEndDate && new Date(u.subscriptionEndDate).getTime() > Date.now();
  const isFlagged = u.isSubscribed === true;

  // Ativo se tem plano pago OU data futura OU flag de assinatura
  return hasPaidPlan || hasFutureEndDate || isFlagged;
};

/**
 * Usuário com plano vencido: tinha acesso pago (plano != free ou flag ligada)
 * e a data de validade já passou. Admin nunca é considerado vencido.
 */
export const isExpiredSubscriber = (u: AppUser | null | undefined): boolean => {
  if (!u) return false;
  if (u.role === "admin") return false;
  if (!u.subscriptionEndDate) return false;

  const ts = new Date(u.subscriptionEndDate).getTime();
  if (!Number.isFinite(ts)) return false;

  return ts <= Date.now();
};

/** Data de vencimento formatada em pt-BR (para banners/avisos). */
export const formatExpirationDate = (u: AppUser | null | undefined): string => {
  if (!u?.subscriptionEndDate) return "";
  try {
    return new Date(u.subscriptionEndDate).toLocaleDateString("pt-BR");
  } catch {
    return "";
  }
};

/**
 * Para usuários inativos, divide deterministicamente entre /planos e /pagamento
 * baseado no hash do user id (50/50 estável por usuário).
 */
const inactiveDestination = (u: AppUser): "/planos" | "/pagamento" => {
  const id = u.id || u.email || "";
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 2 === 0 ? "/planos" : "/pagamento";
};

/**
 * Decide o destino pós-login.
 * - Admin / assinante ativo → fromPath || /dashboard
 * - Inativo                 → 50% /planos, 50% /pagamento (estável por user id)
 * - Sem usuário             → /login
 */
export const decideAuthDestination = (
  u: AppUser | null | undefined,
  fromPath?: string | null
): string => {
  if (!u) return "/login";
  if (isActiveSubscriber(u)) return fromPath || "/dashboard";
  return inactiveDestination(u);
};
